/*----------------------------------
- GLOSSARY BUBBLE EDGE FLIP
----------------------------------*/

/**
 * @docs docs/features/glossary
 *
 * Flips a bubble to open leftwards when opening rightwards would run it past the viewport.
 *
 * The definition itself is server-rendered and disclosed by CSS, which is the part that has to
 * work without JavaScript: a card no crawler reads is the failure this feature exists to avoid.
 * Where the card *sits* is a different question, and CSS alone cannot answer it, because no CSS
 * rule can know that this particular word happens to sit near the right edge of the screen. So
 * the flip is the one enhancement: with scripting off the card still opens, it can just clip on
 * a term in the last few centimetres of a line.
 *
 * One delegated listener for the whole document, installed once. A listener per bubble would mean
 * a few hundred of them on a Compare page, and measuring every card at load would thrash layout
 * for cards nobody opens.
 */

/*----------------------------------
- CONSTANTS
----------------------------------*/

/** The page gutter. A card is flipped before it touches it, not after it leaves the screen. */
const VIEWPORT_MARGIN = 16;

/**
 * The card's widest possible box, matching `max-width` in `shared.css`.
 *
 * Read from a constant rather than measured, because the card is `display: none` until it opens
 * and a hidden element measures zero. Forcing it visible to measure would cost a reflow on every
 * hover, and the answer would not be better: a card narrower than this can only fit more easily.
 */
const CARD_MAX_WIDTH = 320;

let installed = false;

/*----------------------------------
- MEASUREMENT
----------------------------------*/

const alignCard = (term: HTMLElement) => {
    // Decided from the start position every time: a resize, or a term measured while its section
    // was still animating in, would otherwise leave a stale decision behind.
    term.removeAttribute('data-card-align');

    const width = Math.min(CARD_MAX_WIDTH, window.innerWidth - VIEWPORT_MARGIN * 2);
    const termBox = term.getBoundingClientRect();

    const overflowsRight = termBox.left + width > window.innerWidth - VIEWPORT_MARGIN;
    // Flipping only helps when the card actually fits on the other side.
    const fitsFlipped = termBox.right - width >= VIEWPORT_MARGIN;

    if (overflowsRight && fitsFlipped) term.setAttribute('data-card-align', 'end');
};

/*----------------------------------
- INSTALL
----------------------------------*/

export const installGlossaryEdgeFlip = () => {
    if (installed || typeof document === 'undefined') return;
    installed = true;

    const handle = (event: Event) => {
        const target = event.target as Element | null;
        const term = target?.closest?.('.ud-term');
        if (term instanceof HTMLElement) alignCard(term);
    };

    // `pointerover` and `focusin` both bubble, unlike `mouseenter` and `focus`, so one listener
    // on the document covers every bubble on the page, including ones added later.
    document.addEventListener('pointerover', handle, { passive: true });
    document.addEventListener('focusin', handle);
};
